import React, { useEffect, useState } from "react";
import { ImageIcon } from "lucide-react";
import { cn } from "lib/utils";
import { getImageUrl } from "utils/common";
import { isImageCached, preloadImage } from "utils/imageCache";

const CachedImage = ({
  src,
  alt = "",
  className,
  fallbackClassName,
  showFallbackIcon = true,
  ...props
}) => {
  const imageUrl = getImageUrl(src);
  const alreadyCached = isImageCached(imageUrl);
  const [loaded, setLoaded] = useState(alreadyCached);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!imageUrl) {
      setLoaded(false);
      setFailed(true);
      return;
    }

    let cancelled = false;
    setFailed(false);
    setLoaded(isImageCached(imageUrl));

    preloadImage(imageUrl).then((ok) => {
      if (cancelled) return;
      if (ok) {
        setLoaded(true);
        setFailed(false);
      } else {
        setFailed(true);
        setLoaded(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  if (!imageUrl || failed) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-gray-100 dark:bg-gray-800",
          fallbackClassName || className
        )}
      >
        {showFallbackIcon && <ImageIcon className="w-5 h-5 text-gray-400" />}
      </div>
    );
  }

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {!loaded && (
        <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-gray-700" />
      )}
      <img
        src={imageUrl}
        alt={alt}
        loading="lazy"
        decoding="async"
        className={cn(
          "h-full w-full object-cover transition-opacity duration-200",
          loaded ? "opacity-100" : "opacity-0",
          className
        )}
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
        {...props}
      />
    </div>
  );
};

export default CachedImage;
